import { FC, useState, useMemo, useEffect } from 'react';
import { Grid, Button, Box, Typography, CircularProgress } from '@mui/material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { Add, ArrowBack, Delete } from '@mui/icons-material';
import {
  useAcademicsSchedulesContext,
  useAcademicsSchedulesActions,
} from '../../contexts/academicsSchedules.context';
import {
  getSchedulesGridColumns,
  getSchedulePeriodsGridColumns,
} from './academicsSchedules.helpers';
import {
  IAcademicsSchedule,
  ISchedulePeriod,
} from '../../../../../apis/academics/academicsSchedules.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { EditButton } from 'src/views/components/simple/AllButtons';
import { useAcademicsService } from 'src/apis/academics/useAcademicsService';
import { useQueryClient } from '@tanstack/react-query';
import { getAcademicsSchedulesKey } from '../../hooks/useAcademicsSchedulesHook';

enum EScheduleView {
  SCHEDULES = 'schedules',
  PERIODS = 'periods',
}

interface AcademicsSchedulesGridProps {
  isLoading?: boolean;
}

const AcademicsSchedulesGrid: FC<AcademicsSchedulesGridProps> = ({
  isLoading: propIsLoading,
}) => {
  const { translateText } = useTranslation();
  const { schedulesQuery, getActiveSchedules, academicsSchedulesAppState } =
    useAcademicsSchedulesContext();
  const {
    setIsManageScheduleOpen,
    setSelectedSchedule,
    setSelectedScheduleId,
    setIsCreateMode,
    setIsEditMode,
  } = useAcademicsSchedulesActions();

  const [viewType, setViewType] = useState<EScheduleView>(
    EScheduleView.SCHEDULES
  );
  const [schedules, setSchedules] = useState<IAcademicsSchedule[]>([]);
  const [periods, setPeriods] = useState<ISchedulePeriod[]>([]);

  const selectedSchedule = academicsSchedulesAppState.selectedSchedule;

  const schedulesGridColumns = useMemo(
    () => getSchedulesGridColumns(translateText),
    [translateText]
  );
  const periodsGridColumns = useMemo(
    () => getSchedulePeriodsGridColumns(translateText),
    [translateText]
  );

  const isLoading = propIsLoading || schedulesQuery.isLoading;

  useEffect(() => {
    if (schedulesQuery.data) {
      const activeSchedules = getActiveSchedules();
      setSchedules(activeSchedules);
    }
  }, [schedulesQuery.data, getActiveSchedules]);

  const handleAddSchedule = () => {
    if (viewType === EScheduleView.SCHEDULES) {
      setIsManageScheduleOpen(true);
      setIsCreateMode(true);
      setIsEditMode(false);
      setSelectedSchedule(null);
      setSelectedScheduleId(null);
    } else {
      // Handle add period logic
      console.log('Add period');
    }
  };

  const handleRowClick = (params: any) => {
    if (viewType === EScheduleView.SCHEDULES) {
      const schedule = schedules.find((s) => s.id === params.row.id);
      if (schedule) {
        setSelectedSchedule(schedule);
        setSelectedScheduleId(schedule.id);
        setPeriods(schedule.periods);
        setViewType(EScheduleView.PERIODS);
      }
    }
  };

  const handleBackToSchedules = () => {
    setViewType(EScheduleView.SCHEDULES);
    setSelectedSchedule(null);
    setSelectedScheduleId(null);
  };

  const handleEditSchedule = () => {
    if (selectedSchedule) {
      setIsManageScheduleOpen(true);
      setIsCreateMode(false);
      setIsEditMode(true);
      setSelectedScheduleId(selectedSchedule.id);
    }
  };

  const { deleteAcademicsSchedule } = useAcademicsService();
  const queryClient = useQueryClient();
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);

  const handleDeleteSchedule = async () => {
    if (!selectedSchedule) return;
    setIsDeletingSchedule(true);
    try {
      await deleteAcademicsSchedule(selectedSchedule.id);
      queryClient.invalidateQueries({ queryKey: [getAcademicsSchedulesKey] });
      handleBackToSchedules();
    } finally {
      setIsDeletingSchedule(false);
    }
  };

  return (
    <Grid
      container
      spacing={0}
      sx={{ height: '100%' }}
      data-testid="academics-schedules-grid"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent:
            viewType === EScheduleView.PERIODS && selectedSchedule
              ? 'space-between'
              : 'end',
          gap: { xs: 2, sm: 0 },
          mb: { xs: 2, sm: 2 },
        }}
        data-testid="academics-schedules-header"
      >
        {viewType === EScheduleView.PERIODS && selectedSchedule ? (
          <>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              gap={{ xs: 1, sm: 2 }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToSchedules}
                sx={{
                  minHeight: { xs: '44px', sm: '36px' },
                  fontSize: { xs: '16px', sm: '14px' },
                }}
                data-testid="back-to-schedules-button"
              >
                {translateText('academics.schedules.backToSchedules')}
              </Button>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  wordBreak: 'break-word',
                }}
              >
                {selectedSchedule.name} -{' '}
                {translateText('academics.schedules.periods')}
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <EditButton
                label={translateText('academics.schedules.editSchedule')}
                onClickHandler={handleEditSchedule}
              />
              <Button
                variant="outlined"
                color="error"
                startIcon={isDeletingSchedule ? <CircularProgress size={16} /> : <Delete />}
                onClick={handleDeleteSchedule}
                disabled={isDeletingSchedule}
                sx={{ minHeight: { xs: '44px', sm: '36px' } }}
                data-testid="delete-schedule-button"
              >
                Delete
              </Button>
              </Box>
            </Box>
          </>
        ) : (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleAddSchedule}
            sx={{
              minHeight: { xs: '44px', sm: '36px' },
              fontSize: { xs: '16px', sm: '14px' },
              width: { xs: '100%', sm: 'auto' },
            }}
            data-testid="add-schedule-button"
          >
            {viewType === EScheduleView.SCHEDULES
              ? translateText('academics.schedules.addSchedule')
              : translateText('academics.schedules.addPeriod')}
          </Button>
        )}
      </Grid>

      {viewType === EScheduleView.PERIODS && selectedSchedule && (
        <Grid item xs={12}>
          <Box sx={{ mb: { xs: 2, sm: 2 } }}>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
            >
              {translateText('academics.schedules.showingPeriods', {
                count: periods.length,
                name: selectedSchedule.name,
              })}
            </Typography>
          </Box>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        data-testid="academics-schedules-grid-container"
        sx={{
          height: 'calc(100vh - 250px)',
          overflow: 'auto',
        }}
      >
        <PaginatedGrid
          data-testid="academics-schedules-grid"
          rows={viewType === EScheduleView.SCHEDULES ? schedules : periods}
          columns={
            viewType === EScheduleView.SCHEDULES
              ? schedulesGridColumns
              : periodsGridColumns
          }
          loading={isLoading}
          onRowClick={
            viewType === EScheduleView.SCHEDULES ? handleRowClick : undefined
          }
          getRowId={(row) => row.id}
          hideColumnsOnMobile={
            viewType === EScheduleView.SCHEDULES
              ? ['term', 'createdAt', 'updatedAt']
              : ['teacherId', 'classId', 'room']
          }
        />
      </Grid>
    </Grid>
  );
};

export default AcademicsSchedulesGrid;
